import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { dialogStyle } from "../../../shared/styles/dialogStyles";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark } from "../../../shared/styles/buttonStyles";
import { useTranslation } from "react-i18next";

const OrderByFilter = ({ selectedOrderBy, handleChange }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [dialogOpen, setDialogOpen] = useState(false);
  const buildOptions = (activeValue) => [
    { value: "recommended", active: activeValue === "recommended" },
    { value: "distance", active: activeValue === "distance" },
    { value: "rating", active: activeValue === "rating" },
  ];
  const [optionsState, setOptionsState] = useState(() => buildOptions(selectedOrderBy));
  const [selectedOption, setSelectedOption] = useState(() => optionsState.find((option) => option.active));

  useEffect(() => {
    const updatedOptions = buildOptions(selectedOrderBy);
    setOptionsState(updatedOptions);
    setSelectedOption(updatedOptions.find((option) => option.active));
  }, [selectedOrderBy]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const fontColor = {
    color: "#003C59",
  };

  const handleOptionClick = (item) => {
    const updatedOptions = optionsState.map((option) => {
      return { ...option, active: option.value === item.value };
    });
    setOptionsState(updatedOptions);
  };
  
  const handleApply = () => {
    setSelectedOption(optionsState.find((option) => option.active));
    handleChange(optionsState.find((option) => option.active));
    handleDialogClose();
  };

  const getDisplayText = () => {
    if (selectedOption) {
      return t(`orderBy.${selectedOption.value}`) || selectedOption.value;
    }
    return t("orderBy.select");
  };

  return (
    <>
      <Box
        onClick={handleDialogOpen}
        sx={{
          width: "155px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "4px 4px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "white",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Typography variant='body1' sx={{ ...fontColor, fontWeight: 700, fontSize: "0.775rem" }}>
          {getDisplayText()}
        </Typography>
        <ExpandMoreIcon />
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='sm' fullWidth sx={{ ...dialogStyle }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: "bold", ...fontColor }}>
              {t("orderBy.header")}
            </Typography>
            <Typography variant='body2' sx={{ color: "text.secondary", marginTop: 1, ...fontColor }}>
              {t("orderBy.description")}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} sx={{ padding: 0, marginTop: "-4px" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: isMobile ? "16px" : "36px" }}>
          {optionsState.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ ...fontColor, mb: 2, fontWeight: "bold" }}>
                {t("orderBy.title")}
              </Typography>
              {optionsState.map((item, index) => (
                <Alert
                  key={index}
                  icon={item.active ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                  sx={{
                    backgroundColor: item.active ? "rgb(214, 252, 255)" : "transparent",
                    color: "#003C59",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    marginBottom: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: item.active ? "rgb(150, 229, 242)" : "#f5f5f5",
                    },
                    "& .MuiAlert-icon": {
                      color: "#003C59",
                      alignSelf: "center",
                    },
                    "& .MuiAlert-message": {
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      width: "100%",
                    },
                  }}
                  onClick={() => handleOptionClick(item)}
                >
                  <Typography variant='body1' sx={{fontWeight: 500, fontSize: "0.875rem",}}>
                    {t(`orderBy.${item.value}`)}
                  </Typography>
                  <Typography variant='body2' sx={{opacity: 0.8, mt: 0.5, fontSize: "0.75rem",}}>
                    {t(`orderBy.${item.value}Description`)}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#003C59",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "16px 24px",
            gap: 2,
            zIndex: 1,
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant='outlined'
            sx={{ ...altCloseButtonStyleDark, flex: 1 }}
          >
            {t("common.close")}
          </Button>
          <Button onClick={handleApply} variant='contained' sx={{ ...altChooseButtonStyleTurquoise, flex: 1 }}>
            {t("common.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderByFilter;
